import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { api, imageUrl } from "../lib/api";
import { SITE_URL } from "../lib/constants";
import { projectOnderwerp } from "../data/portfolio";
import { BeforeAfterSlider } from "../components/sections/BeforeAfterSlider";
import { ProjectHero } from "../components/portfolio/ProjectHero";
import { ProjectFacts } from "../components/portfolio/ProjectFacts";
import { ProjectReview, ProjectStory, hasStory } from "../components/portfolio/ProjectStory";
import { ProjectGallery } from "../components/portfolio/ProjectGallery";
import { ProjectLinks } from "../components/portfolio/ProjectLinks";
import { CrossLink } from "../components/portfolio/PortfolioHero";
import { ProductEndCTA } from "../components/product/RelatedProducts";

const ytEmbed = (url) => url?.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{11})/)?.[1];
const absImg = (path) => { const u = imageUrl(path); return u.startsWith("/") ? SITE_URL + u : u; };

const BeforeAfter = ({ p }) => (p.before_image?.path && p.after_image?.path ? (
  <section aria-labelledby="voor-na-title" className="section-tight" data-testid="before-after-slider">
    <div className="container-tight">
      <h2 id="voor-na-title" className="h2">Voor en na</h2>
      <p className="mt-2 text-slate-600">Sleep de schuif om de situatie vóór en na de montage te vergelijken.</p>
      <div className="mt-6"><BeforeAfterSlider before={imageUrl(p.before_image.path)} after={imageUrl(p.after_image.path)}
        beforeAlt={p.before_image.alt || `${p.title}: situatie vóór de montage`} afterAlt={p.after_image.alt || `${p.title}: resultaat na de montage`} /></div>
    </div>
  </section>
) : null);

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    let on = true;
    api.get(`/projects/${slug}`).then((r) => on && setP(r.data)).catch(() => on && setP(null));
    return () => { on = false; };
  }, [slug]);
  const path = `/projecten/${p?.slug || slug}`;
  const crumbs = [{ label: "Home", href: "/" }, { label: "Projecten", href: "/projecten" }, { label: p?.title || "Project", href: path }];
  useSEO({
    title: p?.seo_title || (p ? `${p.title} | Maas Kozijn` : "Project | Maas Kozijn"),
    description: p?.meta_description || p?.summary || p?.werk?.slice(0, 155) || "Bekijk dit door Maas Kozijn uitgevoerde kozijnproject.",
    path, ogImage: p ? absImg(p.hero_image?.path) : undefined,
    jsonLd: p ? { "@context": "https://schema.org", "@type": "Article", headline: p.title, datePublished: p.published_at, dateModified: p.updated_at,
      image: absImg(p.hero_image?.path), author: { "@type": "Organization", name: "Maas Kozijn" }, publisher: { "@type": "Organization", name: "Maas Kozijn" },
      mainEntityOfPage: SITE_URL + path, ...(p.plaats ? { contentLocation: { "@type": "Place", name: p.plaats } } : {}) } : null,
    breadcrumbs: p ? buildBreadcrumbsJsonLd(crumbs) : null,
  });
  if (p === undefined) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-maas" /></div>;
  if (p === null) return <Navigate to="/projecten" replace />;
  const embed = ytEmbed(p.youtube_url);
  return (
    <article data-testid="project-detail-page">
      <ProjectHero p={p} crumbs={crumbs} />
      <section className="section" aria-label="Over dit project">
        {hasStory(p) ? (
          <div className="container-tight grid gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="space-y-10 lg:col-span-7"><ProjectStory p={p} /><ProjectReview p={p} /></div>
            <aside className="lg:col-span-5"><div className="lg:sticky lg:top-24"><ProjectFacts p={p} /></div></aside>
          </div>
        ) : <div className="container-tight"><div className="max-w-3xl"><ProjectFacts p={p} /></div></div>}
      </section>
      <BeforeAfter p={p} />
      <ProjectGallery p={p} />
      {embed && (
        <section className="section-tight" aria-label="Video">
          <div className="container-tight"><div className="aspect-video overflow-hidden rounded-2xl border border-slate-200">
            <iframe src={`https://www.youtube-nocookie.com/embed/${embed}`} title={p.title} className="h-full w-full" loading="lazy" allow="encrypted-media; picture-in-picture" allowFullScreen />
          </div></div>
        </section>
      )}
      <ProjectLinks p={p} />
      <ProductEndCTA title="Wilt u vergelijkbare kozijnen? Ontvang vrijblijvend een prijsindicatie." onderwerp={projectOnderwerp(p)} params={{ project: p.slug }} preselected={!!projectOnderwerp(p)} />
      <CrossLink to="/projecten/inspiratie" text="Meer ideeën opdoen? Bekijk onze inspiratiegalerij." label="Naar de inspiratiegalerij" testid="project-inspiration-link" />
    </article>
  );
}
